import ErrorUtil, { OVER_TIME_ERROR } from '@/utils/service/error';
import { HttpMethod } from '@/enums/netEnum';
import Axios from './Axios';
import { useUserStore } from '@/store/modules/user';
import { useGlobalStore } from '@/store/modules/global';
import { AxiosError } from 'axios';
import { useI18n } from '@/hooks/web/useI18n';
import { useLocaleStore } from '@/store/modules/locale';
import { ElLoading } from 'element-plus'
// import store from '@/store';

const axios = Axios();

export type Config = {
    error?: string | ((res: any) => void);
    hideLoading?: boolean;
    needToken?: boolean;
    timeout?: number;
};

export default class HttpUtil {
    /**
     * 获取Http请求的scheme, http:// 或者 https://
     * @return String scheme
     */
    private static getScheme() {
        return 'http://';
    }

    /**
     * 获取Http请求的endpoint，格式 ip:port
     * @return String endpoint
     */
    private static getEndpoint() {
        const { t } = useI18n();
        throw new Error(t('common.sysErrText.classNeedInherited'));
    }

    /**
     * 获取Http请求的参数，也是封装统一参数格式的方法
     * @param  Any params 请求参数
     * @return Any        请求参数
     */
    private static getParams(params: any) {
        return params;
    }

    /**
     * 统一发送get请求方法
     * @param  {String} path    请求路径
     * @param  {Object} params  请求参数
     * @param  {Object} headers 请求首部
     * @param  {Object} options fetch.api 其他可配置信息
     * @return {Object}         调用结果
     */
    static async get<T = any>(
        path: string,
        params = {},
        config: Config = {},
        headers = {},
        options = {},
    ): Promise<any> {
        return await this.call<T>(
            path,
            this.getParams(params),
            config,
            headers,
            options,
            HttpMethod.GET,
        );
    }

    /**
     * 统一发送post请求方法
     * @param  {String} path    请求路径
     * @param  {Object} params  请求参数
     * @param  {Object} headers 请求首部
     * @param  {Object} options fetch.api 其他可配置信息
     * @return {Object}         调用结果
     */
    static async post<T = any>(
        path: string,
        params = {},
        config: Config = {},
        headers = {},
        options = {},
    ): Promise<any> {
        return await this.call<T>(
            path,
            this.getParams(params),
            config,
            headers,
            options,
            HttpMethod.POST,
        );
    }

    /**
     * 因为json常用，所以多出来一个统一发送post + json格式的请求方法
     * @param  {String} path    请求路径
     * @param  {Object} params  请求参数
     * @param  {Object} headers 请求首部
     * @param  {Object} options fetch.api 其他可配置信息
     * @return {Object}         调用结果
     */
    static async json<T = any>(
        path: string,
        params = {},
        config: Config = {},
        headers = {},
        options = {},
    ): Promise<any> {
        return await this.call<T>(
            path,
            this.getParams(params),
            config,
            headers,
            options,
            HttpMethod.JSON,
        );
    }

    /**
     * 通用发送http请求的方法
     * @param  {String} path    请求路径
     * @param  {Object} params  请求参数
     * @param  {Object} headers 请求首部
     * @param  {Object} options fetch.api 其他可配置信息
     * @return {Object}         调用结果
     */
    static async call<T>(
        path: string,
        params = {},
        config: Config = {},
        headers: any = {},
        options = {},
        method: HttpMethod = HttpMethod.GET,
    ) {
        const { t } = useI18n();
        const url = path;
        const { error, hideLoading = true, needToken = true, timeout } = config;

        if (useGlobalStore().serverForbid) {
            ErrorUtil.net({ error: 'server forbid', data: params });
            return Promise.reject('server forbid');
        }
        const userStore = useUserStore();
        headers = {
            ...headers,
            localetype: useLocaleStore().getLocale,
        }
        if (needToken) {
            if (userStore.userInfo.token) {
                headers = {
                    ...headers,
                    token: userStore.userInfo.token,
                };
            } else {
                ErrorUtil.net({ error: 'no token', data: params });
                return Promise.reject('no token');
            }
        }

        method = method.toUpperCase() as any as HttpMethod;

        switch (method) {
            case HttpMethod.POST:
                headers['Content-Type'] = headers['Content-Type'] || 'application/json';
                break;
            case HttpMethod.JSON:
                headers['Content-Type'] = headers['Content-Type'] || 'application/json';
                break;
            case HttpMethod.GET:
                params = { ...params, t_: Date.now() };
                break;
            default:
                break;
        }

        let requestOptions: any = {
            url,
            data: params,
            method,
            headers,
            // timeout: 1000 * 5,
        };
        // console.log(url);
        const clearLoading = this.makeLoading(hideLoading, timeout || 6 * 1000);
        const request = (axios(requestOptions) as any)
            .then((data: any) =>
                data && data.status === 200
                    ? data.data
                    : (() => {
                          throw { error: error || t('common.errText.badNet') };
                      })(),
            )
            .then((data: any) =>
                data && data.code === 10000
                    ? data.data
                    : (() => {
                          throw { error: error, data };
                      })(),
            )
            .catch((payload: any) => {
                throw payload instanceof AxiosError
                    ? { error: error || t('common.errText.badNet') }
                    : payload;
            })

        return await Promise.race<T>([
            request,
            this.makeTimeoutPromise(timeout || 1000 * 6, error),
        ]).catch<void>((payload) => {
            ErrorUtil.net(payload) as unknown as T;
        }).finally(() => {
            clearLoading?.();
        });
    }
    /**
     * 生成超时时间的Promise
     * @param  {String|Number} timeout 超时时间
     * @return {Promise} 超时Promise
     */
    private static async makeTimeoutPromise(timeout: number, error?: any) {
        const { t } = useI18n();
        return await new Promise((_, rj) =>
            setTimeout(() => {
                rj({ error: error || t('common.errText.badNet'), data: OVER_TIME_ERROR });
            }, timeout),
        );
    }

    /**
     * 生成加载 loading
     * @param hideLoading
     * @param timeout 
     * @returns 
     */
    private static makeLoading(hideLoading: boolean, timeout: number) {
        let instance;
        const showLoading = !hideLoading;
        if (showLoading) {
            instance = ElLoading.service({ text: 'loading' });
            return () => { instance.close() }
        }
        if (timeout < 3000 || showLoading) return;
        const timer = setTimeout(() => { 
            instance = ElLoading.service({ text: 'loading' })
         }, 2 * 1000);
        const clearLoading = () => {
            instance?.close?.();
            clearTimeout(timer);
        }
        return clearLoading;
    }
}
